import { useEffect, useState } from 'react';
import { Alert } from '@playerbase/ui';

import { useShell } from './use-shell.js';

// Inline (non-toast) status banners for the background jobs the shell tracks.
// They read shell state, so the same banner can sit on any screen and stays
// in sync across them (and survives a reload, since the shell hydrates).
// Each renders nothing when its job is idle.

// Braille-dot frames borrowed from the long line of terminal spinners
// (Claude Code, npm, cargo, ...). Reads as a single glyph next to the
// banner copy and signals "still working" without taking up real estate.
const SPINNER_FRAMES = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏'];
const SPINNER_INTERVAL_MS = 90;

// Decorative animated glyph. The surrounding banner copy carries the
// accessible label, so the screen reader doesn't need each frame.
function SpinnerGlyph(): JSX.Element {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    // Respect users who've opted out of motion. The static first frame
    // still reads as a status indicator alongside the copy. Guard
    // matchMedia for jsdom, which doesn't ship it by default.
    const reduceMotion =
      typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return;
    const id = window.setInterval(
      () => setFrame((n) => (n + 1) % SPINNER_FRAMES.length),
      SPINNER_INTERVAL_MS,
    );
    return () => window.clearInterval(id);
  }, []);
  return (
    <span aria-hidden className="mr-2 inline-block font-mono">
      {SPINNER_FRAMES[frame]}
    </span>
  );
}

// "Importing channel history…" - shown while a Discord backfill is running.
// Used on the watched-channels screen (where it's triggered) and the Feedback
// screen (where the imported messages land).
export function BackfillStatusBanner(): JSX.Element | null {
  const { activeBackfillJobs } = useShell();
  if (activeBackfillJobs.length === 0) return null;

  const channelsDone = activeBackfillJobs.reduce((n, j) => n + j.channelsDone, 0);
  const channelsTotal = activeBackfillJobs.reduce((n, j) => n + j.channelsTotal, 0);

  return (
    <Alert tone="info">
      <SpinnerGlyph />
      Importing channel history…
      {channelsTotal > 0 ? ` (${channelsDone}/${channelsTotal} channels)` : ''}
    </Alert>
  );
}

// "Refreshing insights…" - shown while an insights refresh is in progress,
// spanning both message collection and the theme/bucket pass. Used on the
// Insights screen.
export function InsightsStatusBanner(): JSX.Element | null {
  const { insightsRefreshing } = useShell();
  if (!insightsRefreshing) return null;

  return (
    <Alert tone="info">
      <SpinnerGlyph />
      Refreshing insights…
    </Alert>
  );
}
